import { IdentityServiceV2 } from 'Public_old/src/app/Services/api/v2/identity.service';
import { AuthContextService } from 'Public_old/src/app/Services/authContext.service';
import { GeneralService } from 'Public_old/src/app/Services/general.service';
import { ScheduledDataService } from 'Public_old/src/app/Services/scheduled-data';
import { SpaEventService } from 'Public_old/src/app/Services/spaEvents.service';
import { AfterViewInit, Component, ElementRef, NgZone, OnChanges, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { createRoot, Root } from 'react-dom/client';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Header } from 'src/components/header/mod';
import { CellulaProviderWrapper } from './cellula_provider_wrapper.component';

const containerElementName = 'reactHeaderComponent';

@Component({
	standalone: false,
	styleUrls: ['./css/header.wrapper.scss'],
	selector: 'header-component',
	template: `<div class="header-wrapper-container" #${containerElementName}></div>`,
	encapsulation: ViewEncapsulation.None,
})
export class HeaderWrapperComponent implements OnChanges, OnDestroy, AfterViewInit {
	@ViewChild(containerElementName, { static: false }) containerRef: ElementRef;

	private readonly routerSubscription: Subscription;

	private root: Root;

	constructor(
		private readonly router: Router,
		private readonly spaEventService: SpaEventService,
		private readonly identityService: IdentityServiceV2,
		private readonly ngZone: NgZone,
		private readonly scheduledDataService: ScheduledDataService,
		private readonly authContextService: AuthContextService,
		private readonly generalService: GeneralService,
	) {
		let isFirstNavigation = true;

		this.routerSubscription = this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
			if (isFirstNavigation) {
				isFirstNavigation = false;

				return;
			}

			this.render();
		});
	}

	ngOnChanges(): void {
		this.render();
	}

	ngAfterViewInit() {
		this.render();
	}

	ngOnDestroy() {
		this.routerSubscription.unsubscribe();

		this.root?.unmount();
	}

	private setLanguage(languageCode: string): void {
		localStorage.setItem('languageCode', languageCode);

		if (this.authContextService.isSignedIn()) {
			this.generalService.setlanguage(languageCode).subscribe(
				() => {
					window.location.reload();
				},
				// biome-ignore lint/suspicious/noEmptyBlockStatements: suppressed due to migration - should be fixed
				() => {},
			);
		} else {
			window.location.reload();
		}
	}

	// biome-ignore lint/correctness/noUnusedPrivateClassMembers: suppressed due to migration - should be fixed
	private registerOutsideCollectorListener(_: string, callback: (data: number) => void) {
		const listeners: Subscription[] = [];

		listeners.push(
			this.scheduledDataService.onGetCases.subscribe((caseList) => {
				// Copy pasta from menu
				// Check notification count
				const issues = caseList.map(($case) => $case.UnhandledIssues.length).reduce((a, b) => a + b, 0);
				callback(issues);
			}),
			this.spaEventService.onSignedOut.subscribe(() => {
				callback(0);
			}),
		);

		return {
			unsubscribe: () => {
				listeners.forEach((listener) => listener.unsubscribe());
			},
		};
	}

	private signIn(): void {
		this.spaEventService.onSignInRequired.emit({
			leave: false,
			target: this.router.url,
			reason: null,
		});
	}

	private signOut(): void {
		this.identityService.signOut().subscribe(
			({ data }) => {
				this.spaEventService.onSignedOut.emit({ redirectUrl: data.RedirectUrl });
			},
			() => {
				window.location.reload();
			},
		);
	}

	private navigate(route: string): void {
		this.router.navigateByUrl(route);
	}

	private async render() {
		const root = createRoot(this.containerRef.nativeElement);
		this.root = root;

		const forwardSignIn = () => {
			this.ngZone.run(() => {
				this.signIn();
			});
		};
		const forwardSignOut = () => {
			this.ngZone.run(() => {
				this.signOut();
			});
		};
		const forwardNavigate = (route) => {
			this.ngZone.run(() => {
				this.navigate(route);
			});
		};
		const forwardChangeLanguage = (code) => {
			this.ngZone.run(() => {
				this.setLanguage(code);
			});
		};

		this.ngZone.runOutsideAngular(() => {
			this.root.render(
				<CellulaProviderWrapper>
					<Header
						__TEMP__navigate={forwardNavigate}
						__TEMP__onSignIn={forwardSignIn}
						__TEMP__onSignOut={forwardSignOut}
						__TEMP__changeLanguage={forwardChangeLanguage}
					/>
				</CellulaProviderWrapper>,
			);
		});
	}
}
